// dataUtils.js
// Utility functions for data processing and calculations

/**
 * Constants for order statuses
 */
export const STATUS_TYPES = {
    SUCCESS: ['Completed', 'Collected', 'Finished'],
    FAILED: ['Cancelled', 'Failed', 'Error']
  };
  
  /**
   * Default VAT rate
   */
  export const DEFAULT_VAT_RATE = 0.20; // 20% VAT
  
  /**
   * Check if a product is a cleaning service
   * @param {Object} product - Product object to check
   * @returns {boolean} - True if product is a cleaning service
   */
  export const isCleaningService = (product) => {
    // Check product name for cleaning service indicators
    const productName = (product.order_products_name || product.name || '').toLowerCase();
    return productName.includes('cleaning') || 
           productName.includes('service') || 
           (product.category_id === 'cleaning');
  };
  
  /**
   * Calculate price without VAT
   * @param {number} priceWithVAT - Price including VAT
   * @param {number} vatRate - VAT rate (default: 20%)
   * @returns {number} - Price excluding VAT
   */
  export const calculatePriceWithoutVAT = (priceWithVAT, vatRate = DEFAULT_VAT_RATE) => {
    const price = parseFloat(priceWithVAT || 0);
    return price / (1 + vatRate);
  };
  
  /**
   * Calculate VAT amount
   * @param {number} priceWithVAT - Price including VAT
   * @param {number} vatRate - VAT rate (default: 20%)
   * @returns {number} - VAT amount
   */
  export const calculateVATAmount = (priceWithVAT, vatRate = DEFAULT_VAT_RATE) => {
    const price = parseFloat(priceWithVAT || 0);
    return price - (price / (1 + vatRate));
  };
  
  /**
   * Process orders based on filters (cleaning services exclusion and VAT)
   * @param {Array} orders - Original order data
   * @param {Object} filters - Filter settings
   * @returns {Array} - Processed order data
   */
  export const processOrders = (orders, filters) => {
    const { excludeCleaningServices, showPricesWithoutVAT } = filters;
    
    if (!orders || orders.length === 0) return [];
    
    // Make a deep copy to avoid mutation issues
    const processed = JSON.parse(JSON.stringify(orders));
    
    return processed.map(order => {
      const orderCopy = { ...order };
      
      // Exclude cleaning services if needed
      if (excludeCleaningServices && orderCopy.order_products && orderCopy.order_products.length > 0) {
        // Identify cleaning service items
        const cleaningItems = orderCopy.order_products.filter(product => isCleaningService(product));
        
        // Calculate cleaning service costs
        const cleaningTotal = cleaningItems.reduce((sum, item) => {
          const itemPrice = parseFloat(item.order_products_price || 0);
          const itemQty = parseInt(item.order_products_qty || 1); 
          return sum + (itemPrice * itemQty);
        }, 0);
        
        // Store original data for reference
        orderCopy._original_products = [...orderCopy.order_products];
        orderCopy._original_total = orderCopy.order_total;
        
        // Update order products to exclude cleaning services
        orderCopy.order_products = orderCopy.order_products.filter(product => !isCleaningService(product));
        
        // Subtract the cleaning cost from the total
        const originalTotal = parseFloat(orderCopy.order_total || 0);
        orderCopy.order_total = Math.max(0, originalTotal - cleaningTotal).toString();
        
        // Store the amount excluded for reference
        orderCopy._cleaning_amount_excluded = cleaningTotal;
      }
      
      // Calculate VAT amounts if showing without VAT
      if (showPricesWithoutVAT) {
        // Use the current order total (which may already exclude cleaning services)
        const orderTotal = parseFloat(orderCopy.order_total || 0);
        
        // Calculate VAT amount and price without VAT
        const vatAmount = calculateVATAmount(orderTotal);
        const totalWithoutVAT = calculatePriceWithoutVAT(orderTotal);
        
        // Store original total with VAT for reference
        orderCopy._original_total_with_vat = orderCopy.order_total;
        
        // Update order total to exclude VAT
        orderCopy.order_total = totalWithoutVAT.toString();
        orderCopy.vat_amount = vatAmount;
        
        // Process each product's price
        if (orderCopy.order_products) {
          orderCopy.order_products = orderCopy.order_products.map(product => {
            const productCopy = { ...product };
            const price = parseFloat(productCopy.order_products_price || 0);
            const priceWithoutVAT = calculatePriceWithoutVAT(price);
            const productVAT = calculateVATAmount(price);
            
            productCopy._original_price_with_vat = productCopy.order_products_price;
            productCopy.order_products_price = priceWithoutVAT.toString();
            productCopy.vat_amount = productVAT;
            
            return productCopy;
          });
        }
      }
      
      return orderCopy;
    });
  };
  
  /**
   * Calculate sales metrics from order data
   * @param {Array} orders - Processed order data
   * @param {Object} options - Options for calculation
   * @returns {Object} - Sales metrics object
   */
  export const calculateMetrics = (orders, options = {}) => {
    const { showPricesWithoutVAT = false } = options;
    
    if (!orders || orders.length === 0) {
      return {
        totalOrders: 0,
        totalRevenue: 0,
        successfulOrders: 0,
        failedOrders: 0,
        averageOrderValue: 0,
        totalVAT: 0,
        totalCleaningAmount: 0
      };
    }
  
    // Calculate basic metrics
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + parseFloat(order.order_total || 0), 0);
    const totalVAT = showPricesWithoutVAT ? 
      orders.reduce((sum, order) => sum + parseFloat(order.vat_amount || 0), 0) : 0;
    
    // Count orders by status
    const successfulOrders = orders.filter(order => 
      STATUS_TYPES.SUCCESS.includes(order.order_status)
    ).length;
    
    const failedOrders = orders.filter(order => 
      STATUS_TYPES.FAILED.includes(order.order_status)
    ).length;
    
    // Calculate average order value
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    
    // Calculate total cleaning amount excluded (if available)
    const totalCleaningAmount = orders.reduce((sum, order) => 
      sum + parseFloat(order._cleaning_amount_excluded || 0), 0);
  
    return {
      totalOrders,
      totalRevenue,
      successfulOrders,
      failedOrders,
      averageOrderValue,
      totalVAT,
      totalCleaningAmount
    };
  };
  
  /**
   * Prepare data for property summary view
   * @param {Array} orders - Processed order data 
   * @param {Object} options - Options for data preparation
   * @returns {Array} - Property summary data
   */
  export const preparePropertySummaryData = (orders, options = {}) => {
    const { showPricesWithoutVAT = false } = options;
    
    if (!orders || orders.length === 0) return [];
    
    // Group by property
    const groupedByProperty = orders.reduce((acc, order) => {
      const propertyKey = order.order_property?.property_title || 'Unknown Property';
      const propertyId = order.order_property?.id || 'Unknown';
      const propertyAddress = order.order_property?.apartment_address || order.delivery_address_1 || 'Unknown';
      
      if (!acc[propertyKey]) {
        acc[propertyKey] = {
          propertyId: propertyId,
          propertyTitle: propertyKey,
          propertyAddress: propertyAddress,
          totalOrders: 0,
          totalRevenue: 0,
          totalVAT: 0,
          successfulOrders: 0,
          failedOrders: 0,
          pendingOrders: 0,
        };
      }
      
      // Add order stats to property
      acc[propertyKey].totalOrders += 1;
      acc[propertyKey].totalRevenue += parseFloat(order.order_total || 0);
      
      if (showPricesWithoutVAT) {
        acc[propertyKey].totalVAT += parseFloat(order.vat_amount || 0);
      }
      
      // Count by status
      if (STATUS_TYPES.SUCCESS.includes(order.order_status)) {
        acc[propertyKey].successfulOrders += 1;
      } else if (STATUS_TYPES.FAILED.includes(order.order_status)) {
        acc[propertyKey].failedOrders += 1;
      } else {
        acc[propertyKey].pendingOrders += 1;
      }
      
      return acc;
    }, {});
    
    // Convert to array for table and sort by revenue
    return Object.values(groupedByProperty).sort((a, b) => b.totalRevenue - a.totalRevenue);
  };
  
  /**
   * Prepare data for charts in the overview tab
   * @param {Array} orders - Processed order data
   * @param {Object} options - Options for chart data preparation
   * @returns {Object} - Chart data for overview tab
   */
  export const prepareChartData = (orders, options = {}) => {
    if (!orders || orders.length === 0) {
      return {
        timeSeries: [],
        statusDistribution: [],
        propertyRevenue: []
      };
    }
    
    // Group by date for time series chart
    const groupedByDate = orders.reduce((acc, order) => {
      const date = new Date(order.property_date).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = {
          date,
          revenue: 0,
          vat: 0,
          orders: 0,
          successfulOrders: 0,
          failedOrders: 0
        };
      }
      
      // Add revenue
      acc[date].revenue += parseFloat(order.order_total || 0);
      
      // Add VAT if available
      if (order.vat_amount) {
        acc[date].vat += parseFloat(order.vat_amount || 0);
      }
      
      // Count orders
      acc[date].orders += 1;
      
      // Count by status
      if (STATUS_TYPES.SUCCESS.includes(order.order_status)) {
        acc[date].successfulOrders += 1;
      } else if (STATUS_TYPES.FAILED.includes(order.order_status)) {
        acc[date].failedOrders += 1;
      }
      
      return acc;
    }, {});
    
    // Convert to array and sort by date
    const timeSeries = Object.values(groupedByDate).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    // Count by status for status distribution chart
    const statusCount = orders.reduce((acc, order) => {
      const status = order.order_status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    
    const statusDistribution = Object.entries(statusCount)
      .map(([name, value]) => ({ name, value }));
    
    // Property revenue data for pie chart
    const propertyRevenueData = {};
    
    orders.forEach(order => {
      const property = order.order_property?.property_title || 'Unknown Property';
      
      if (!propertyRevenueData[property]) {
        propertyRevenueData[property] = {
          revenue: 0,
          vat: 0
        };
      }
      
      propertyRevenueData[property].revenue += parseFloat(order.order_total || 0);
      
      if (order.vat_amount) {
        propertyRevenueData[property].vat += parseFloat(order.vat_amount || 0);
      }
    });
    
    // Convert to array, sort by revenue, and limit to top 6
    const propertyRevenue = Object.entries(propertyRevenueData)
      .map(([name, data]) => ({ 
        name, 
        value: data.revenue,
        vat: data.vat,
        totalWithVAT: data.revenue + data.vat
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
    
    return {
      timeSeries,
      statusDistribution,
      propertyRevenue
    };
  };
  

  /**
   * Format date range for file naming
   * @param {Array} dateRange - Start and end date array
   * @returns {string} - Formatted date range string
   */
  export const formatDateRangeForFilename = (dateRange) => {
    if (!dateRange || dateRange.length !== 2) return '';
    return `${dateRange[0]}_to_${dateRange[1]}`;
  };
  
  /**
   * Format currency for display
   * @param {number} value - Number to format as currency
   * @returns {string} - Formatted currency string
   */
  export const formatCurrency = (value) => {
    return `£${parseFloat(value || 0).toFixed(2)}`;
  };
  
  /**
   * Get default date range based on view mode
   * @param {string} mode - Date view mode ('today', 'week', 'month')
   * @param {Date} date - Base date
   * @returns {Array} - Array with start and end dates as strings
   */
  export const getDefaultDateRange = (mode, date) => {
    const startDate = new Date(date);
    const endDate = new Date(date);
  
    if (mode === 'today') {
      // For "today" mode, both start and end date are the same date
    } else if (mode === 'week') {
      const day = startDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      startDate.setDate(startDate.getDate() - day); // Set to previous Sunday
      endDate.setDate(startDate.getDate() + 6); // Set to Saturday
    } else if (mode === 'month') {
      startDate.setDate(1); // First day of the month
      endDate.setMonth(endDate.getMonth() + 1, 0); // Last day of the month
    }
  
    return [
      formatDate(startDate),
      formatDate(endDate)
    ];
  };

  // Add this function to your dataUtils.js file or create it in SalesReport.js

/**
 * Prepare order details data based on view mode - matches UI structure exactly
 * @param {Array} reportData - Raw report data
 * @param {string} viewMode - 'default', 'items', or 'grouped'
 * @returns {Object} Processed data structure
 */
export const prepareOrderDetailsData = (reportData, viewMode) => {
  if (viewMode === 'grouped') {
    // Group orders by property - exactly like the UI does
    const groupedByProperty = reportData.reduce((acc, order) => {
      const propertyKey = order.order_property?.property_title || 'Unknown Property';
      const propertyAddress = order.order_property?.apartment_address || order.delivery_address_1 || '';
      
      if (!acc[propertyKey]) {
        acc[propertyKey] = {
          property: propertyKey,
          propertyAddress,
          orders: [],
          totalRevenue: 0,
          totalVAT: 0,
          ordersCount: 0,
          itemsCount: 0
        };
      }
      
      const orderTotal = parseFloat(order.order_total || 0);
      const orderVAT = parseFloat(order.vat_amount || 0);
      const itemsCount = order.order_products ? order.order_products.length : 0;
      
      acc[propertyKey].orders.push({
        ...order,
        calculatedTotal: orderTotal - orderVAT, // excl VAT
        calculatedVAT: orderVAT
      });
      acc[propertyKey].totalRevenue += orderTotal - orderVAT; // excl VAT for consistency
      acc[propertyKey].totalVAT += orderVAT;
      acc[propertyKey].ordersCount += 1;
      acc[propertyKey].itemsCount += itemsCount;
      
      return acc;
    }, {});
    
    // Convert to flat array with property headers and orders
    const dataSource = [];
    
    Object.entries(groupedByProperty).forEach(([propertyKey, propertyData]) => {
      // Add property group header
      dataSource.push({
        key: `property-${propertyKey}`,
        property: propertyKey,
        propertyAddress: propertyData.propertyAddress,
        ordersCount: propertyData.ordersCount,
        itemsCount: propertyData.itemsCount,
        totalRevenue: propertyData.totalRevenue,
        totalVAT: propertyData.totalVAT,
        isPropertyGroup: true
      });
      
      // Add orders under this property
      propertyData.orders.forEach(order => {
        dataSource.push({
          ...order,
          key: `order-${order.id}`,
          property: propertyKey,
          isPropertyGroup: false
        });
      });
    });
    
    return {
      dataSource,
      groupedData: groupedByProperty
    };
  }
  else if (viewMode === 'items') {
    // For items view, we return the orders as-is since expansion is handled by the table
    return {
      dataSource: reportData.map(order => ({
        ...order,
        key: order.id
      }))
    };
  }
  else {
    // Default flat view
    return {
      dataSource: reportData.map(order => ({
        ...order,
        key: order.id
      }))
    };
  }
};

  
  /**
   * Format date as YYYY-MM-DD
   * @param {Date} date - Date to format
   * @returns {string} - Formatted date string
   */
  export const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };